import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetSuppliesQuery, useDeleteSupplyMutation } from "../../slices/suppliesApiSlice";
import { toast } from "react-toastify";
import { useState } from "react";

const PurchaseListScreen = () => {
  const [filter, setFilter] = useState("");
  const [productSearch, setProductSearch] = useState('')

  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data, isLoading, error, refetch } = useGetSuppliesQuery(pageNumber);
  const [deleteSupply, { isLoading: loadingDelete }] = useDeleteSupplyMutation();

  
  // 1) Date‐range filter, 2) general filter (note/user/customer/product), 
  // 3) product‐only filter
  const filterSupplies = () => {
    if (!data?.supplies) return []

    // 1) date range
    let result = data.supplies.filter((supply) => {
      const d = new Date(supply.date)
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate   || d <= new Date(endDate))
      )
    })

    // 2) general text filter
    if (filter.trim()) {
      const lf = filter.toLowerCase()
      result = result.filter((supply) => {
        return (
          supply.note?.toLowerCase().includes(lf) ||
          supply.username?.toLowerCase().includes(lf) ||
          supply.supplierDetails?.name?.toLowerCase().includes(lf) ||
          supply.supplyItems.some((it) => it.name.toLowerCase().includes(lf))
        )
      })
    }

    

    // 3) product‐only filter
    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase()
      result = result
        .map((supply) => ({
          ...supply,
          supplyItems: supply.supplyItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((supply) => supply.supplyItems.length > 0)
    }

    return result
  }

  const calculateTotal = (keyFn) =>
    filterSupplies().reduce(
      (total, supply) =>
        total + supply.supplyItems.reduce((sub, item) => sub + keyFn(item), 0),
      0
    );

  const exportToExcel = (supplies, filename = "Supplies.xlsx") => {
    const rows = supplies.flatMap((supply) =>
      supply.supplyItems.map((item) => ({
        Date: new Date(supply.date).toLocaleDateString(),
        Product: item.name,
        Quantity: item.qty,
        Revenue: item.qty * item.price,
        Cost: item.itemCost,
        Profit: item.profit,
        Note: supply.note || "",
        Supplier: supply.supplierDetails?.name || "",
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Supplies");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), filename);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this supply?")) {
      try {
        await deleteSupply(id).unwrap();
        toast.success("Supply deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete supply.");
      }
    }
  };

  return (
    <>
      <Row className="align-items-center mb-4">
        <Col><h1>Supplies</h1></Col>
        <Col className="text-end">
          <Button variant="success" onClick={() => exportToExcel(filterSupplies())}>
            Export to Excel
          </Button>
          <Link to='/admin/supplysearchReplace' className='btn btn-light my-3'>
            Search
          </Link>
        </Col>
      </Row>

      {/* 🔍 Filters */}
      <Row className="mb-3">
        <Col md={3}><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></Col>
        <Col md={3}><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></Col>
        <Col md={3}><Form.Control value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by note, supplier, product" /></Col>
        
      </Row>

      {/* New product‐only filter */}
            <Row className="mb-4">
              <Col md={4}>
                <Form.Control
                  type="text"
                  placeholder="Filter by product name only"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </Col>
        </Row>

      <Row className="mb-4">
        <Col className="text-end"><Button onClick={refetch}>Reset</Button></Col>
      </Row>

      {/* 📊 Totals */}
      <Row className="mb-3">
        <Col md={3}><h4>TRev: ${calculateTotal(i => i.qty * i.price).toFixed(2)}</h4></Col>
        <Col md={3}><h4>TQty: ${calculateTotal(i => i.qty).toFixed(2)}</h4></Col>
        <Col md={3}><h4>TCost: ${calculateTotal(i => i.itemCost).toFixed(2)}</h4></Col>
        <Col md={3} className="text-end"><h4>TProfit: ${calculateTotal(i => i.profit).toFixed(2)}</h4></Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || "Something went wrong."}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>REV</th>
                <th>COST</th>
                <th>PROFIT</th>
                <th>NOTE</th>
                <th>SUPPLIER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
  {filterSupplies().map((supply) =>
    supply.supplyItems.map((item) => (
      <tr key={`${supply._id}-${item.name}`}>
        <td>{new Date(supply.date).toLocaleDateString()}</td>
        <td>{item.name}</td>
        <td>{item.qty}</td>
        <td>${(item.qty * item.price).toFixed(2)}</td>
        <td>${item.itemCost}</td>
        <td>${item.profit.toFixed(2)}</td>
        <td>{supply.note || "N/A"}</td>
        <td>{supply.supplierDetails?.name || "N/A"}</td>
        <td>
          {item.images?.[0] && (
            <img
              src={item.images[0]}
              alt="Product"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          )}
        </td>
        <td>
          <Button as={Link} to={`/supply/${supply._id}`} variant="light" className="btn-sm">
            Details
          </Button>
          <Button as={Link} to={`/supply/${supply._id}/edit`} variant="light" className="btn-sm mx-2">
            Edit
          </Button>
          <Button variant="danger" className="btn-sm" onClick={() => handleDelete(supply._id)}>
            Delete
          </Button>
        </td>
      </tr>
    ))
  )}
</tbody>
            
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => setPageNumber(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default PurchaseListScreen;